import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatEuro } from "@/lib/api";
import { UploadCloud, Loader2, Sparkles, Save, X, Camera, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const Campo = ({ label, children }) => (
  <div>
    <Label className="text-xs tracking-[0.05em]">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const NuovoAssegno = () => {
  const navigate = useNavigate();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [immagine, setImmagine] = useState(null);
  const [analizzando, setAnalizzando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({
    valore: "",
    numero_assegno: "",
    banca: "",
    data_assegno: "",
    data_scadenza: "",
    ricevuto_da: "",
    data_ricezione: new Date().toISOString().slice(0, 10),
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Carica un file immagine (JPG o PNG)");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setImmagine(dataUrl);
    setAnalizzando(true);
    toast.info("Analisi AI dell'assegno in corso...");
    try {
      const dati = await api.estrai(dataUrl);
      setForm((f) => ({
        ...f,
        valore: dati.valore ?? f.valore,
        numero_assegno: dati.numero_assegno ?? f.numero_assegno,
        banca: dati.banca ?? f.banca,
        data_assegno: dati.data_assegno ?? f.data_assegno,
      }));
      toast.success("Dati estratti automaticamente! Verifica e completa.");
    } catch (e) {
      toast.error("Estrazione automatica non riuscita. Inserisci i dati manualmente.");
    } finally {
      setAnalizzando(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const salva = async () => {
    if (!form.ricevuto_da) {
      toast.error("Indica da chi hai ricevuto l'assegno");
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        valore: form.valore ? parseFloat(form.valore) : null,
        numero_assegno: form.numero_assegno || null,
        banca: form.banca || null,
        data_assegno: form.data_assegno || null,
        data_scadenza: form.data_scadenza || null,
        ricevuto_da: form.ricevuto_da || null,
        data_ricezione: form.data_ricezione || null,
        immagine,
        stato: "ricevuto",
      };
      await api.crea(payload);
      toast.success("Assegno salvato in archivio");
      navigate("/archivio");
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Nuovo Assegno</h1>
        <p className="text-[#5C5F58] mt-1">Scatta o carica la foto: i dati vengono estratti automaticamente</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Upload */}
        <div className="space-y-4">
          {!immagine ? (
            <div
              data-testid="dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="border-2 border-dashed border-[#E5E2DC] bg-[#F9F8F6] p-12 text-center rounded-xl flex flex-col items-center justify-center min-h-[300px]"
            >
              <div className="w-14 h-14 rounded-full bg-white border border-[#E5E2DC] flex items-center justify-center mb-4">
                <UploadCloud className="w-7 h-7 text-[#1C3F35]" strokeWidth={1.6} />
              </div>
              <p className="font-heading font-semibold text-[#1A1C18]">Aggiungi la foto dell'assegno</p>
              <p className="text-sm text-[#5C5F58] mt-1 mb-5">Scatta una foto o importa un'immagine già presente sul telefono (JPG, PNG)</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  data-testid="btn-scatta-foto"
                  onClick={() => cameraRef.current?.click()}
                  className="bg-[#1C3F35] hover:bg-[#132A23] text-white"
                >
                  <Camera className="w-4 h-4 mr-1.5" /> Scatta foto
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="btn-importa-galleria"
                  onClick={() => galleryRef.current?.click()}
                  className="border-[#E5E2DC] bg-white hover:bg-[#F2F0EB] text-[#1A1C18]"
                >
                  <ImageIcon className="w-4 h-4 mr-1.5" /> Importa dalla galleria
                </Button>
              </div>
            </div>
          ) : (
            <div className="relative rounded-xl border border-[#E5E2DC] bg-white p-3">
              <img src={immagine} alt="Assegno" className="w-full rounded-lg object-contain max-h-[340px]" data-testid="preview-immagine" />
              <button
                onClick={() => { setImmagine(null); }}
                data-testid="rimuovi-immagine"
                className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white border border-[#E5E2DC] flex items-center justify-center hover:bg-[#FBE3DA]"
              >
                <X className="w-4 h-4" />
              </button>
              {analizzando && (
                <div className="absolute inset-3 rounded-lg bg-white/80 backdrop-blur flex flex-col items-center justify-center">
                  <Loader2 className="w-8 h-8 text-[#1C3F35] animate-spin" />
                  <p className="mt-3 text-sm font-medium text-[#1C3F35] flex items-center gap-1">
                    <Sparkles className="w-4 h-4" /> Estrazione dati con AI...
                  </p>
                </div>
              )}
            </div>
          )}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid="file-input-camera"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <input
            ref={galleryRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>

        {/* Form */}
        <div className="bg-white border border-[#E5E2DC] rounded-xl p-6 shadow-sm space-y-5">
          <p className="font-heading font-semibold text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#1C3F35]" /> Dati estratti dall'assegno
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Campo label="Valore (€)">
              <Input type="number" step="0.01" data-testid="campo-valore" value={form.valore} onChange={(e) => upd("valore", e.target.value)} placeholder="0,00" />
            </Campo>
            <Campo label="Numero assegno">
              <Input data-testid="campo-numero" value={form.numero_assegno} onChange={(e) => upd("numero_assegno", e.target.value)} placeholder="000000000" />
            </Campo>
            <Campo label="Banca">
              <Input data-testid="campo-banca" value={form.banca} onChange={(e) => upd("banca", e.target.value)} placeholder="Nome banca" />
            </Campo>
            <Campo label="Data assegno">
              <Input type="date" data-testid="campo-data-assegno" value={form.data_assegno ? form.data_assegno.slice(0,10) : ""} onChange={(e) => upd("data_assegno", e.target.value)} />
            </Campo>
          </div>

          <div className="border-t border-[#E5E2DC] pt-5 space-y-4">
            <p className="font-heading font-semibold text-sm">Dati ricezione & scadenza</p>
            <div className="grid grid-cols-2 gap-4">
              <Campo label="Ricevuto da *">
                <Input data-testid="campo-ricevuto-da" value={form.ricevuto_da} onChange={(e) => upd("ricevuto_da", e.target.value)} placeholder="Es. Mario Rossi" />
              </Campo>
              <Campo label="Data ricezione">
                <Input type="date" data-testid="campo-data-ricezione" value={form.data_ricezione} onChange={(e) => upd("data_ricezione", e.target.value)} />
              </Campo>
              <Campo label="Data scadenza">
                <Input type="date" data-testid="campo-data-scadenza" value={form.data_scadenza} onChange={(e) => upd("data_scadenza", e.target.value)} />
              </Campo>
            </div>
            <p className="text-xs text-[#5C5F58]">Riceverai un promemoria 3 giorni prima della scadenza.</p>
          </div>

          <Button
            onClick={salva}
            disabled={salvando || analizzando}
            data-testid="btn-salva-assegno"
            className="w-full bg-[#1C3F35] hover:bg-[#132A23] text-white"
          >
            <Save className="w-4 h-4 mr-1.5" /> {salvando ? "Salvataggio..." : "Salva in archivio"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NuovoAssegno;
